import { inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';

import { I18nService } from '@core/i18n-legacy';
import { QuestionWithOptionsService } from '@tellent-ask-hr/dynamic-forms-smart-ui';
import type {
  DefineFormData,
  DynamicFormConfig,
  UnwrapSignal,
} from '@tellent-ask-hr/dynamic-forms-types';
import type { DropdownTemplateItem } from '@tellent-ask-hr/templates-types';

interface RespondConfig {
  question: string;
  hint: string;
  required: boolean;
  options: Array<{ value: string }>;
}

type RespondComponentType =
  import('@tellent-ask-hr/dynamic-forms-smart-ui').RespondSelectSingleComponent<
    RespondConfig['options'][number]['value']
  >;

export class DropdownConfig implements DynamicFormConfig {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly questionWithOptionsService = inject(QuestionWithOptionsService);
  private readonly i18n = inject(I18nService);

  public readonly kind: DropdownTemplateItem['type'] = 'dropdown';
  public readonly defineConfig: DynamicFormConfig['defineConfig'] = {
    name: this.i18n.translate('tlc.ask_hr.form_template_create.configs.dropdown'),
    icon: 'list',
  };

  public readonly components = {
    define: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.QuestionWithOptionsComponent),
    respond: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.RespondSelectSingleComponent),
    present: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.TextPresenterComponent),
  };

  public readonly defineForm = (data?: DefineFormData) =>
    this.questionWithOptionsService.createQuestionForm(data);

  public readonly respondConfig = (
    data: RespondConfig,
  ): UnwrapSignal<RespondComponentType['respondConfig']> => ({ ...data, kind: 'dropdown' });

  public readonly respondForm = (config: {
    required: boolean;
  }): UnwrapSignal<RespondComponentType['respondForm']> =>
    this.formBuilder.group({
      value: this.formBuilder.control<string | null>(
        null,
        config.required ? Validators.required : [],
      ),
    });

  public readonly presentConfig = (
    data: DropdownTemplateItem,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').TextPresenterComponent['config']
  > => ({
    heading: data.name,
    value: data.value,
    kind: {
      type: 'text',
      multiline: false,
    },
  });
}
